import React, { useCallback, useRef } from 'react';
import * as Location from 'expo-location';
import { LocationAccuracy } from 'expo-location';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { Map } from '../Map';
import { MapControls } from './MapControls';

import type { Props } from './MapWithControls.types';
import type { MapRef } from '@vis.gl/react-maplibre';

// ────────────────────────────────────────────────────────────────────────────────

export const MapWithControls = (props: Props) => {
  const {
    disableGeolocateUserBtn,
    onGeolocateUserBtnPress,
    onZoomInPress,
    onZoomOutPress,
    ...mapProps
  } = props;

  const { formatMessage } = useIntl();

  // ─── Refs ────────────────────────────────────────────────────────────

  const mapRefWeb = useRef<MapRef>(null);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const getCurrentLocation = useCallback(async () => {
    const { status } = await Location.requestForegroundPermissionsAsync();

    if (status !== 'granted') {
      alert(formatMessage(messages.locationPermissionDenied));

      return;
    }

    return Location.getCurrentPositionAsync({ accuracy: LocationAccuracy.Low });
  }, [formatMessage]);

  const handleGeolocateUserBtnPress = useCallback(async () => {
    const getLocation = onGeolocateUserBtnPress ?? getCurrentLocation;
    const geolocation = await getLocation();

    if (!geolocation?.coords) return;

    const { latitude: lat, longitude: lng } = geolocation.coords;

    const map = mapRefWeb.current?.getMap();

    console.log('map', map);

    if (map) {
      map.flyTo({ center: [lng, lat], duration: 1000 });
    }
  }, [getCurrentLocation, onGeolocateUserBtnPress]);

  const handleZoomInPress = useCallback(() => {
    onZoomInPress?.();

    const map = mapRefWeb.current?.getMap();
    if (!map) return;

    const currentZoom = map.getZoom();
    const maxZoom = map.getMaxZoom();

    if (currentZoom >= maxZoom) return;

    map.zoomTo(currentZoom + 1, { duration: 240 });
  }, [onZoomInPress]);

  const handleZoomOutPress = useCallback(() => {
    onZoomOutPress?.();

    const map = mapRefWeb.current?.getMap();
    if (!map) return;

    const currentZoom = map.getZoom();
    const minZoom = map.getMinZoom();

    if (currentZoom <= minZoom) return;

    map.zoomTo(currentZoom - 1, { duration: 240 });
  }, [onZoomOutPress]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <Map mapRefWeb={mapRefWeb} {...mapProps} />

      <MapControls
        onGeolocateUserBtnPress={handleGeolocateUserBtnPress}
        onZoomInPress={handleZoomInPress}
        onZoomOutPress={handleZoomOutPress}
        disableGeolocateUserBtn={disableGeolocateUserBtn}
      />
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationPermissionDenied: {
    defaultMessage: 'Please enable location permissions to use this feature.',
    description: 'Map With Controls | Location Permission Denied Alert | Message',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
